import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field'
import { answerCoverageNudge, ConfirmInfoRefusal, type CoverageNudge } from '@/models/confirmInfo'
import { refreshInsuranceApplication } from '@/models/insuranceApplication'
import { useConfirmInfoStep } from './useConfirmInfoStep'

interface CoverageNudgesProps {
  orgId: string
  applicationId: string | undefined
  nudges: CoverageNudge[]
}

/** Coverage a contractor's answers suggest but they didn't keep: one tap adds it, and its questions follow in the chat. */
export function CoverageNudges({ orgId, applicationId, nudges }: CoverageNudgesProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { run, pending, error, refusedCode } = useConfirmInfoStep(orgId)

  useEffect(() => {
    if (refusedCode !== ConfirmInfoRefusal.QUESTIONS_IN_CHAT || !applicationId) return
    // The chat asks the added coverage's questions; /application has to see the chat moved first.
    const toChat = () => void navigate('/application')
    refreshInsuranceApplication(queryClient, applicationId).then(toChat, toChat)
  }, [refusedCode, applicationId, navigate, queryClient])

  if (nudges.length === 0) return null
  return (
    <div className="flex flex-col gap-3">
      {nudges.map((nudge) => (
        <Banner
          key={nudge.coverage}
          variant="info"
          action={
            <div className="flex gap-2">
              <Button
                size="sm"
                loading={pending ? 'Adding…' : undefined}
                onClick={() => void run(() => answerCoverageNudge(orgId, nudge.coverage, true))}
              >
                {nudge.addLabel}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={pending}
                onClick={() => void run(() => answerCoverageNudge(orgId, nudge.coverage, false))}
              >
                No thanks
              </Button>
            </div>
          }
        >
          {nudge.message}
        </Banner>
      ))}
      {refusedCode !== ConfirmInfoRefusal.QUESTIONS_IN_CHAT && (
        <FieldError className="text-xs">{error}</FieldError>
      )}
    </div>
  )
}
